/**
 * Advanced Analytics Dashboard
 * - Geographic heat map
 * - Time-based heatmap  
 * - Custom date range picker
 * - Real-time live counter
 * - PDF report export
 */
import React, { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Globe, Clock, Calendar, Download, RefreshCw,
  Activity, TrendingUp, MapPin, Zap, FileText, Filter,
  ChevronDown, BarChart3, Users, Smartphone, Monitor, Tablet
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup
} from 'react-simple-maps';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// World map GeoJSON URL
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// World map component using react-simple-maps
const WorldMapHeatmap = memo(({ locations }) => {
  const maxCount = Math.max(...locations.map(l => l.count), 1);
  
  return (
    <div className="bg-slate-900 rounded-xl overflow-hidden">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 120,
          center: [0, 30]
        }}
        style={{ width: '100%', height: 'auto', aspectRatio: '2/1' }}
      >
        <ZoomableGroup>
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#374151"
                  stroke="#4B5563"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none' },
                    hover: { fill: '#4B5563', outline: 'none' },
                    pressed: { outline: 'none' }
                  }}
                />
              ))
            }
          </Geographies>
          
          {/* Heat map markers */}
          {locations.map((loc, idx) => {
            const intensity = loc.count / maxCount;
            const size = 6 + intensity * 18;
            
            return (
              <Marker key={idx} coordinates={[loc.lng, loc.lat]}>
                <circle
                  r={size}
                  fill={`rgba(99, 102, 241, ${0.4 + intensity * 0.6})`}
                  stroke="#818CF8"
                  strokeWidth={1}
                  style={{
                    filter: `drop-shadow(0 0 ${size}px rgba(99, 102, 241, ${intensity}))`
                  }}
                />
                <title>{`${loc.city}, ${loc.country}: ${loc.count} scans`}</title>
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>
      
      {/* Legend */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-800/50 border-t border-slate-700">
        <div className="flex items-center gap-4 text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-indigo-400/50"></div>
            <span>Low activity</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-indigo-500"></div>
            <span>Medium</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-indigo-600 shadow-lg shadow-indigo-500/50"></div>
            <span>High activity</span>
          </div>
        </div>
        <div className="text-xs text-slate-500">
          {locations.length} location{locations.length !== 1 ? 's' : ''} tracked
        </div>
      </div>
    </div>
  );
});

// Time heatmap component (7 days x 24 hours)
const TimeHeatmap = ({ data }) => {
  if (!data?.heatmap) return null;
  
  const { heatmap, day_labels, hour_labels, peak } = data;
  const maxVal = Math.max(...heatmap.flat(), 1);
  
  // Show only every 3rd hour label to reduce clutter
  const displayHours = [0, 3, 6, 9, 12, 15, 18, 21];
  
  return (
    <div className="bg-card rounded-xl p-4 border border-border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">Scan Activity by Time</h3>
        {peak && (
          <span className="text-xs text-muted-foreground">
            Peak: {peak.day} at {peak.hour} ({peak.count} scans)
          </span>
        )}
      </div>
      
      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          {/* Hour labels */}
          <div className="flex ml-16 mb-1">
            {hour_labels?.map((h, i) => (
              displayHours.includes(i) && (
                <div key={i} className="text-[10px] text-muted-foreground" style={{ width: '16px', marginLeft: i === 0 ? 0 : `${(3 - 1) * 16}px` }}>
                  {h}
                </div>
              )
            ))}
          </div>
          
          {/* Grid */}
          {day_labels?.map((day, dayIdx) => (
            <div key={day} className="flex items-center gap-1 mb-1">
              <div className="w-14 text-xs text-muted-foreground truncate">{day.slice(0, 3)}</div>
              <div className="flex gap-[2px]">
                {heatmap[dayIdx]?.map((val, hourIdx) => (
                  <div
                    key={hourIdx}
                    className="w-4 h-4 rounded-sm transition-colors"
                    style={{
                      backgroundColor: val === 0 
                        ? 'rgba(99, 102, 241, 0.1)' 
                        : `rgba(99, 102, 241, ${0.2 + (val / maxVal) * 0.8})`
                    }}
                    title={`${day} ${hourIdx}:00 - ${val} scans`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Real-time live counter
const LiveCounter = ({ data, onRefresh }) => {
  return (
    <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Live Activity
        </h3>
        <button 
          onClick={onRefresh}
          className="p-2 hover:bg-white/20 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <div className="text-3xl font-bold">{data?.scans_last_5_min || 0}</div>
          <div className="text-xs text-white/70">Last 5 min</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold">{data?.scans_last_hour || 0}</div>
          <div className="text-xs text-white/70">Last hour</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold">{data?.scans_today || 0}</div>
          <div className="text-xs text-white/70">Today</div>
        </div>
      </div>
      
      {data?.active_tags?.length > 0 && (
        <div className="border-t border-white/20 pt-4">
          <div className="text-xs text-white/70 mb-2">Most Active Tags (Last Hour)</div>
          <div className="space-y-1">
            {data.active_tags.slice(0, 3).map((tag, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="truncate max-w-[150px]">{tag.nfc_code}</span>
                <span className="font-semibold">{tag.scans}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main component
const AdvancedAnalytics = () => {
  const navigate = useNavigate();
  const { nfcCode } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [geoData, setGeoData] = useState(null);
  const [timeData, setTimeData] = useState(null);
  const [realtimeData, setRealtimeData] = useState(null);
  const [customData, setCustomData] = useState(null);
  const [reportData, setReportData] = useState(null);
  
  // Date range
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [dateRange, setDateRange] = useState(30);
  
  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [geo, time, realtime] = await Promise.all([
        nfcCode 
          ? axios.get(`${BACKEND_URL}/api/analytics/geo/${nfcCode}?days=${dateRange}`)
          : axios.get(`${BACKEND_URL}/api/analytics/global/geo?days=${dateRange}`),
        nfcCode 
          ? axios.get(`${BACKEND_URL}/api/analytics/time-heatmap/${nfcCode}?days=${dateRange}`)
          : null,
        axios.get(`${BACKEND_URL}/api/analytics/realtime`)
      ]);
      
      setGeoData(geo.data);
      if (time) setTimeData(time.data);
      setRealtimeData(realtime.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [nfcCode, dateRange]);
  
  useEffect(() => {
    loadAllData();
    
    // Auto-refresh realtime data every 30 seconds
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/analytics/realtime`);
        setRealtimeData(res.data);
      } catch (e) {}
    }, 30000);
    
    return () => clearInterval(interval);
  }, [loadAllData]);
  
  const loadCustomRange = async () => {
    if (!nfcCode) {
      toast.error('Select a specific tag for custom date range');
      return;
    }
    
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/analytics/custom-range/${nfcCode}?start_date=${startDate}&end_date=${endDate}`
      );
      setCustomData(res.data);
      toast.success('Custom range loaded');
    } catch (error) {
      toast.error('Failed to load custom range');
    }
  };
  
  const generatePDFReport = async () => {
    if (!nfcCode) {
      toast.error('Select a specific tag for PDF report');
      return;
    }
    
    try {
      toast.loading('Generating report...', { id: 'report' });
      const res = await axios.get(`${BACKEND_URL}/api/analytics/report/${nfcCode}?days=${dateRange}`);
      setReportData(res.data);
      
      // Create printable report
      const reportWindow = window.open('', '_blank');
      reportWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Analytics Report - ${nfcCode}</title>
          <style>
            body { font-family: system-ui, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { color: #4f46e5; }
            .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
            .stat { background: #f3f4f6; padding: 16px; border-radius: 8px; text-align: center; }
            .stat-value { font-size: 24px; font-weight: bold; color: #1f2937; }
            .stat-label { font-size: 12px; color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin: 16px 0; }
            th, td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: left; }
            th { background: #f9fafb; font-weight: 600; }
            .section { margin: 32px 0; }
            .section-title { font-size: 18px; font-weight: 600; margin-bottom: 16px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1>📊 Analytics Report</h1>
          <p><strong>Tag:</strong> ${res.data.tag.nfc_code}</p>
          <p><strong>Period:</strong> ${res.data.report_period}</p>
          <p><strong>Generated:</strong> ${new Date(res.data.generated_at).toLocaleString()}</p>
          
          <div class="stat-grid">
            <div class="stat">
              <div class="stat-value">${res.data.summary.total_scans}</div>
              <div class="stat-label">Total Scans</div>
            </div>
            <div class="stat">
              <div class="stat-value">${res.data.summary.unique_visitors}</div>
              <div class="stat-label">Unique Visitors</div>
            </div>
            <div class="stat">
              <div class="stat-value">${res.data.summary.conversion_rate}</div>
              <div class="stat-label">Unique Rate</div>
            </div>
            <div class="stat">
              <div class="stat-value">${res.data.summary.avg_daily_scans}</div>
              <div class="stat-label">Avg/Day</div>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">📱 Device Breakdown</div>
            <table>
              <tr><th>Device</th><th>Scans</th></tr>
              ${Object.entries(res.data.devices).map(([k, v]) => `<tr><td>${k}</td><td>${v}</td></tr>`).join('')}
            </table>
          </div>
          
          <div class="section">
            <div class="section-title">🔗 Scan Sources</div>
            <table>
              <tr><th>Source</th><th>Scans</th></tr>
              ${Object.entries(res.data.sources).map(([k, v]) => `<tr><td>${k.toUpperCase()}</td><td>${v}</td></tr>`).join('')}
            </table>
          </div>
          
          <div class="section">
            <div class="section-title">🌍 Top Countries</div>
            <table>
              <tr><th>Country</th><th>Scans</th></tr>
              ${Object.entries(res.data.top_countries).map(([k, v]) => `<tr><td>${k}</td><td>${v}</td></tr>`).join('')}
            </table>
          </div>
          
          <div class="section">
            <div class="section-title">🏙️ Top Cities</div>
            <table>
              <tr><th>City</th><th>Scans</th></tr>
              ${Object.entries(res.data.top_cities).map(([k, v]) => `<tr><td>${k}</td><td>${v}</td></tr>`).join('')}
            </table>
          </div>
          
          <p style="margin-top: 40px; color: #9ca3af; font-size: 12px; text-align: center;">
            Generated by ScanIt2Go Analytics
          </p>
          
          <script>window.print();</script>
        </body>
        </html>
      `);
      
      toast.success('Report ready - print dialog opened', { id: 'report' });
    } catch (error) {
      toast.error('Failed to generate report', { id: 'report' });
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-2 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>
              <h1 className="text-2xl font-bold">
                {nfcCode ? `Analytics: ${nfcCode}` : 'Global Analytics'}
              </h1>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Date Range Selector */}
              <select
                value={dateRange}
                onChange={(e) => setDateRange(Number(e.target.value))}
                className="px-3 py-2 bg-secondary rounded-lg text-sm"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
              
              {nfcCode && (
                <button
                  onClick={generatePDFReport}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90"
                >
                  <FileText className="w-4 h-4" />
                  Export PDF
                </button>
              )}
            </div>
          </div>
          
          {/* Live Counter */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            <LiveCounter data={realtimeData} onRefresh={loadAllData} />
            
            {/* Quick Stats */}
            <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-card rounded-xl p-4 border border-border">
                <Globe className="w-5 h-5 text-muted-foreground mb-2" />
                <div className="text-2xl font-bold">{geoData?.total_scans || 0}</div>
                <div className="text-xs text-muted-foreground">Total Scans</div>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border">
                <MapPin className="w-5 h-5 text-muted-foreground mb-2" />
                <div className="text-2xl font-bold">{geoData?.locations?.length || 0}</div>
                <div className="text-xs text-muted-foreground">Locations</div>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border">
                <Users className="w-5 h-5 text-muted-foreground mb-2" />
                <div className="text-2xl font-bold">{Object.keys(geoData?.countries || {}).length}</div>
                <div className="text-xs text-muted-foreground">Countries</div>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border">
                <TrendingUp className="w-5 h-5 text-muted-foreground mb-2" />
                <div className="text-2xl font-bold">
                  {geoData?.total_scans ? Math.round(geoData.total_scans / dateRange) : 0}
                </div>
                <div className="text-xs text-muted-foreground">Avg/Day</div>
              </div>
            </div>
          </div>
          
          {/* Geographic Heat Map */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Geographic Distribution
            </h2>
            <WorldMapHeatmap locations={geoData?.locations || []} />
            
            {/* Top Countries List */}
            {geoData?.countries && Object.keys(geoData.countries).length > 0 && (
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {Object.entries(geoData.countries).slice(0, 8).map(([country, count]) => (
                  <div key={country} className="flex items-center justify-between p-3 bg-card rounded-lg border border-border">
                    <span className="truncate">{country}</span>
                    <span className="font-semibold text-primary">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Time Heatmap (only for specific tag) */}
          {timeData && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Activity by Time
              </h2>
              <TimeHeatmap data={timeData} />
            </div>
          )}
          
          {/* Custom Date Range (only for specific tag) */}
          {nfcCode && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Custom Date Range
              </h2>
              <div className="bg-card rounded-xl p-4 border border-border">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="px-3 py-2 bg-secondary rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="px-3 py-2 bg-secondary rounded-lg text-sm"
                    />
                  </div>
                  <button
                    onClick={loadCustomRange}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90"
                  >
                    Apply
                  </button>
                </div>
                
                {customData && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold">{customData.stats.total_scans}</div>
                        <div className="text-xs text-muted-foreground">Total Scans</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold">{customData.stats.unique_visitors}</div>
                        <div className="text-xs text-muted-foreground">Unique</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold">{customData.stats.avg_per_day}</div>
                        <div className="text-xs text-muted-foreground">Avg/Day</div>
                      </div>
                    </div>
                    
                    {/* Mini chart of daily data */}
                    <div className="flex items-end gap-1 h-20">
                      {Object.entries(customData.by_date || {}).map(([date, count], i) => {
                        const maxCount = Math.max(...Object.values(customData.by_date || {}), 1);
                        const height = (count / maxCount) * 100;
                        return (
                          <div
                            key={date}
                            className="flex-1 bg-primary/60 hover:bg-primary rounded-t transition-colors"
                            style={{ height: `${Math.max(height, 2)}%` }}
                            title={`${date}: ${count} scans`}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Recent Activity */}
          {realtimeData?.recent_scans?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Recent Activity
              </h2>
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-secondary/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Tag</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Device</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {realtimeData.recent_scans.map((scan, i) => (
                      <tr key={i} className="border-b border-border last:border-0 hover:bg-secondary/30">
                        <td className="px-4 py-3 text-sm font-mono truncate max-w-[150px]">{scan.nfc_code}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {scan.city || 'Unknown'}, {scan.country || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {scan.device_type === 'mobile' && <Smartphone className="w-4 h-4 inline" />}
                          {scan.device_type === 'desktop' && <Monitor className="w-4 h-4 inline" />}
                          {scan.device_type === 'tablet' && <Tablet className="w-4 h-4 inline" />}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {new Date(scan.viewed_at).toLocaleTimeString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdvancedAnalytics;
